/* oxlint-disable no-restricted-syntax */
export const isTest = process.env.TEST === 'true';
export const isServer = typeof window === 'undefined';

export const isBuild = process.env.NODE_ENV === 'production';

/**
 * Vercel `next build` 여부. standalone 출력은 Docker에서만 켠다.
 * Turbo strict mode는 `VERCEL`을 명시하지 않으면 잘라내므로, adapter / VERCEL_ENV도 함께 본다.
 */
export const isVercelBuild = Boolean(process.env.VERCEL || process.env.VERCEL_ENV || process.env.NEXT_ADAPTER_PATH);

export const isVercelLive = !isServer && typeof window.__vercel_toolbar !== 'undefined';

declare global {
  interface Window {
    __vercel_toolbar?: {
      isAuthenticated: boolean;
      unmount: () => void;
    };
  }
}

/**
 * 아래와 같이 처리됩니다.
 *
 * - `app-[version].[environment].prex.run` => `[environment].prex.run`
 * - `[version].app-[environment].prex.run` => `[environment].prex.run`
 * - `app.[prod-domain]` => `[prod-domain]`
 * - `app-[version].[prod-domain]` => `[prod-domain]`
 * - `[version].app.[prod-domain]` => `[prod-domain]`
 *
 * 각 변수들이 의미는 다음과 같습니다.
 *
 * - `[environment]`: `sandbox`, `stg`, `prod`
 * - `[version]`: `v1-2-3`, ...
 * - `[prod-domain]`: `flipster.io`, `flipster.me`
 */
export const removeWebviewPrefix = (hostname: string) =>
  hostname.replace(/^app(?:-v(?:\w|-)+)?\.|^v(?:\w|-)+\.app(?:\.|-)|^app-/, '');

const isWebviewHost = (host: string): boolean => {
  if (host.startsWith('localhost')) return host.endsWith(':3001');
  // portless: webview.localhost:1355
  if (host.startsWith('webview.localhost')) return true;
  // starts with "app." or "app-"
  // or includes ".app." or ".app-"
  return !!host.match(/((^|.)app[.-])/);
};

export const convertWebviewToWebUrl = (path: string, fn?: (webview: boolean) => URLSearchParams): string => {
  const { protocol, hostname, host } = window.location;
  const origin = isLocal() ? 'https://stg.prex.run' : `${protocol}//${removeWebviewPrefix(hostname)}`;
  const url = new URL(path, origin);
  if (fn) {
    const params = fn(isWebviewHost(host));
    for (const [name, value] of params.entries()) {
      url.searchParams.append(name, value);
    }
  }
  return url.href;
};

export const gotoFlipster = (path: string, fn?: (webview: boolean) => URLSearchParams) => {
  window.location.href = convertWebviewToWebUrl(path, fn);
};

export const getServerHostname = (): string => {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL ?? '';
  // SSR 시에는 언제나 주입받은 환경변수를 사용함
  // NEXT_PUBLIC_SERVER_URL 은 SSR 에만 사용해야함
  if (isServer) return serverUrl;

  const { protocol, host, hostname } = window.location;
  const isLocalhost = isLocal();
  // 로컬 개발환경에서는 next.config.js 혹은 proxy-server.js에서
  // API 서버를 리버스 프록시하고 있으므로 window.location.host를 그대로 사용함
  if (isLocalhost) return `${protocol}//${host}`;
  // 서버주소상 .prex.website 는 존재할수 없기때문에 주어진 serverUrl 을 사용
  if (serverUrl && hostname.endsWith('.prex.website')) {
    return serverUrl;
  }
  // 타팀에서 웹뷰 테스트가 필요한 경우 사용하는 주소
  // *.yoda.mobi => api-*.yoda.mobi
  // app-*.yoda.mobi => api-*.yoda.mobi
  // qa-test-*.yoda.mobi => api-luke.yoda.mobi
  if (hostname.endsWith('.yoda.mobi')) {
    if (hostname.startsWith('qa-test-')) return `${protocol}//api-luke.yoda.mobi`;
    return `${protocol}//api-${removeWebviewPrefix(hostname)}`;
  }

  //tunneling branch 에서 사용하는 주소
  if (hostname.endsWith('.prex.sh')) {
    return `${protocol}//api-${removeWebviewPrefix(hostname)}`;
  }

  // api.flipster.io
  // api.stg.prex.run
  return `${protocol}//api.${removeWebviewPrefix(hostname)}`;
};

export const isLocal = () => {
  if (isServer) return false;
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
};

export const getServerEnv = () => {
  const serverHostname = getServerHostname();
  if (serverHostname.includes('stg.prex.run')) return 'stg';
  if (serverHostname.includes('sandbox.prex.run')) return 'sandbox';
  if (
    serverHostname.includes('.prex.website') ||
    serverHostname.includes('.yoda.mobi') ||
    serverHostname.includes('.prex.run') ||
    serverHostname.includes('.localhost') ||
    serverHostname === 'localhost'
  )
    return 'dev';
  return 'prod';
};

export const isProd = getServerEnv() === 'prod';
